import { createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search, Check, X } from "lucide-react";

interface WithdrawalItem {
  id: string;
  userId: string;
  amount: number;
  method: string;
  status: string;
  bonusAmount: number;
  createdAt: string;
  processedAt: string | null;
  user: { id: string; email: string; name: string };
}

interface EarningItem {
  id: string;
  userId: string;
  totalEarnings: number;
  availableBalance: number;
  inAppBalance: number;
  withdrawnTotal: number;
  user: { id: string; email: string; name: string };
  pendingWithdrawals: number;
}

const STATUS_COLORS: Record<string, string> = { PENDING: "text-yellow-600", PROCESSING: "text-blue-600", COMPLETED: "text-green-600", FAILED: "text-red-600", CANCELED: "text-gray-400" };
const fmtCurrency = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function EarningsPage() {
  const [withdrawals, setWithdrawals] = React.useState<WithdrawalItem[]>([]);
  const [earnings, setEarnings] = React.useState<EarningItem[]>([]);
  const [wLoading, setWLoading] = React.useState(true);
  const [eLoading, setELoading] = React.useState(true);
  const [wStatusFilter, setWStatusFilter] = React.useState("");
  const [eSearch, setESearch] = React.useState("");

  async function fetchWithdrawals() {
    setWLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (wStatusFilter) params.set("status", wStatusFilter);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/withdrawals?${params}`, { credentials: "include" });
      const json = await res.json();
      setWithdrawals(json.data.withdrawals);
    } catch (e) { console.error(e); } finally { setWLoading(false); }
  }

  async function fetchEarnings() {
    setELoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (eSearch) params.set("search", eSearch);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/earnings?${params}`, { credentials: "include" });
      const json = await res.json();
      setEarnings(json.data.earnings);
    } catch (e) { console.error(e); } finally { setELoading(false); }
  }

  React.useEffect(() => { fetchWithdrawals(); }, [wStatusFilter]);
  React.useEffect(() => { fetchEarnings(); }, [eSearch]);
  React.useEffect(() => { document.title = "Earnings - Admin"; }, []);

  async function handleWithdrawalAction(id: string, status: string) {
    const isReject = status === "CANCELED";
    let reason: string | undefined;
    if (isReject) {
      // The reason is recorded in the admin audit log for this withdrawal.
      const input = prompt("Reason for rejecting this withdrawal:");
      if (input === null) return;
      reason = input.trim();
      if (!reason) { alert("A reason is required to reject a withdrawal."); return; }
    } else if (!confirm("Approve this withdrawal?")) return;
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/withdrawals/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(reason ? { status, reason } : { status }),
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.success) {
        alert(isReject ? "Withdrawal rejected." : "Withdrawal approved.");
      } else if (res.status === 409) {
        alert(`This withdrawal was already settled and cannot be changed. ${json?.error ?? ""}`.trim());
      } else {
        alert(json?.error ?? `Failed to update withdrawal (HTTP ${res.status}).`);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to update withdrawal.");
    }
    fetchWithdrawals();
  }

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Earnings</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Earnings & Withdrawals</h1>
        </div>
      </header>
      <main className="px-4 py-6 md:px-6">
        <Tabs defaultValue="withdrawals">
          <TabsList><TabsTrigger value="withdrawals">Withdrawals</TabsTrigger><TabsTrigger value="balances">Balances</TabsTrigger></TabsList>

          <TabsContent value="withdrawals" className="mt-4 space-y-4">
            <div className="flex gap-2">
              <Badge variant={wStatusFilter === "" ? "default" : "outline"} className="cursor-pointer" onClick={() => setWStatusFilter("")}>All</Badge>
              {["PENDING", "PROCESSING", "COMPLETED", "FAILED", "CANCELED"].map((s) => (
                <Badge key={s} variant={wStatusFilter === s ? "default" : "outline"} className={`cursor-pointer ${wStatusFilter !== s ? STATUS_COLORS[s] : ""}`} onClick={() => setWStatusFilter(wStatusFilter === s ? "" : s)}>{s}</Badge>
              ))}
            </div>
            <div className="relative overflow-hidden rounded-lg border"><div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wLoading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                  ) : withdrawals.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No withdrawals</TableCell></TableRow>
                  ) : withdrawals.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell><div><p className="font-medium">{w.user.name}</p><p className="text-muted-foreground text-xs">{w.user.email}</p></div></TableCell>
                      <TableCell className="text-right font-medium">{fmtCurrency(w.amount)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{w.method}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className={`text-xs ${STATUS_COLORS[w.status] || ""}`}>{w.status}</Badge></TableCell>
                      <TableCell><span className="text-muted-foreground text-xs">{fmtDate(w.createdAt)}</span></TableCell>
                      <TableCell>
                        {w.status === "PENDING" && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleWithdrawalAction(w.id, "COMPLETED")}><Check className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleWithdrawalAction(w.id, "CANCELED")}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div></div>
          </TabsContent>

          <TabsContent value="balances" className="mt-4 space-y-4">
            <div className="relative w-full md:w-[300px]">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input placeholder="Search by email..." className="pl-8" value={eSearch} onChange={(e) => setESearch(e.target.value)} />
            </div>
            <div className="relative overflow-hidden rounded-lg border"><div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Total Earnings</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="text-right">In-App</TableHead>
                    <TableHead className="text-right">Withdrawn</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eLoading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                  ) : earnings.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No earnings found</TableCell></TableRow>
                  ) : earnings.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell><div><p className="font-medium">{e.user.name}</p><p className="text-muted-foreground text-xs">{e.user.email}</p></div></TableCell>
                      <TableCell className="text-right">{fmtCurrency(e.totalEarnings)}</TableCell>
                      <TableCell className="text-right font-medium">{fmtCurrency(e.availableBalance)}</TableCell>
                      <TableCell className="text-right">{fmtCurrency(e.inAppBalance)}</TableCell>
                      <TableCell className="text-right">{fmtCurrency(e.withdrawnTotal)}</TableCell>
                      <TableCell className="text-center">{e.pendingWithdrawals > 0 ? <Badge variant="outline" className="text-yellow-600">{e.pendingWithdrawals}</Badge> : "0"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div></div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/earnings')({
  component: EarningsPage,
})
